import {
  HostBinding,
  Input,
  ChangeDetectorRef,
  Injector,
  Type,
  OnInit,
  Renderer2,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
  NgZone
} from '@angular/core';
import {
  ControlValueAccessor,
  ControlContainer,
  AbstractControl,
  FormControl
} from '@angular/forms';
import { Destroyer, generatetUuId } from '@apps/tools';

export class InputBase extends Destroyer
  implements ControlValueAccessor, OnInit {
  emptyValue = null;

  @Input() id: string = generatetUuId();
  @Input() formControlName: string;
  @Input() formControl: FormControl;
  @Input() labelRef: HTMLLabelElement;

  controlContain: ControlContainer;
  control: AbstractControl;
  renderer: Renderer2;
  ngZone: NgZone;
  cdr: ChangeDetectorRef;

  public _value: any;
  get value(): any {
    return this._value;
  }
  set value(value) {
    this._value = value;
    this.onChange(value);
  }

  @HostBinding('class.is-empty')
  get hostIsEmptyClass() {
    return this.value === this.emptyValue;
  }

  @HostBinding('class.is-required')
  get isRequired(): boolean {
    return this.hasRequiredField(this.control);
  }

  private _disabled: boolean;
  @HostBinding('class.is-disabled')
  get isDisabled(): boolean {
    return (
      this._disabled || (this.control && this.control.status === 'DISABLED')
    );
  }

  @ViewChild('input', { static: false }) input: ElementRef;

  private _active: boolean;
  @HostBinding('class.active') get active(): boolean {
    return this._active;
  }

  @Output() onfocus = new EventEmitter<boolean>();

  constructor(injector: Injector) {
    super();
    this.ngZone = injector.get(NgZone);
    this.controlContain = injector.get<ControlContainer>(
      ControlContainer as Type<ControlContainer>,
      null
    );
    this.renderer = injector.get<Renderer2>(Renderer2 as Type<Renderer2>);
    this.cdr = injector.get<ChangeDetectorRef>(ChangeDetectorRef as Type<
      ChangeDetectorRef
    >);
  }

  onChange: any = (_: any) => {};
  onTouched: any = () => {};

  onInit(): void {}

  ngOnInit() {
    this.initControlContainer();
    this.setLabelFor();
    this.onInit();
  }

  writeValue(delta: any): void {
    this._value = delta;
  }
  registerOnChange(fn: (v: any) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private setLabelFor(): void {
    if (!this.labelRef || this.isDisabled) {
      return;
    }

    this.labelRef.setAttribute('sc-label', '');
    this.labelRef.setAttribute('for', this.id);
  }

  private initControlContainer(): void {
    if (!this.controlContain || (!this.formControlName && !this.formControl)) {
      return;
    }

    this.control =
      this.formControl || this.controlContain.control.get(this.formControlName);
  }

  hasRequiredField(abstractControl): boolean {
    if (!abstractControl) {
      return;
    }

    if (abstractControl.validator) {
      const validator = abstractControl.validator({} as AbstractControl);
      if (validator && validator.required) {
        return true;
      }
    }
    if (abstractControl.controls) {
      for (const controlName in abstractControl.controls) {
        if (abstractControl.controls[controlName]) {
          if (this.hasRequiredField(abstractControl.controls[controlName])) {
            return true;
          }
        }
      }
    }
    return false;
  }

  setDisabledState(isDisabled: boolean): void {
    this.setDisabled(isDisabled);
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        if (this.input && this.input.nativeElement) {
          this.renderer.setProperty(
            this.input.nativeElement,
            'disabled',
            isDisabled
          );
        }
      });
    });
  }

  setActive(actived = true): void {
    this._active = !!actived;
  }

  setDisabled(disabled = true): void {
    this._disabled = !!disabled;
  }
}
